import React from 'react'
import { classnames } from '@halo/_utils/classnames'
import { PageHeader, Well } from '@pushly/aqe/lib/components'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import { noop } from '@pushly/aqe/lib/utils'
import { RouteComponentProps } from 'react-router-dom'
import { Button, Flex, Modal, FormInstance, Form, Input } from 'antd'
import { AppService } from '@halo/services/app.service'
import { makeCrumbClick } from '@halo/_utils/breadcrumb'
import { useService } from '@pushly/aqe/lib/hooks'
import { IUserRole } from '@halo/interfaces/user-role'
import UserRoleService from '@halo/services/user-role.service'
import { useMountedRef } from '@halo/hooks/use-mounted-ref'
import { IAccessPolicy } from '@halo/interfaces/access-policy'
import AccessPolicyService from '@halo/services/access-policy.service'
import FormLayoutRow from '@halo/components/form-layout-row/form-layout-row'
import UserRolePolicyEditor from '@halo/components/user-role-view/user-role-policy-editor'
import { WarningOutlined, QuestionCircleOutlined, EditOutlined } from '@ant-design/icons'
import { RuleObject } from 'antd/es/form'
import moment from 'moment'
import UserRoleEditor from '@halo/components/user-role-view/user-role-editor'
import { isAxiosResponseError } from '@halo/_utils/axios'
import { isInternalRole } from '@halo/_utils/user-role'
import { simpleNotification } from '@halo/_utils/notifications'

const REMOVE_KEY = 'REMOVE'

interface IUserRoleProps extends RouteComponentProps<{ userRoleId: string }> {}

const UserRoleView: React.FunctionComponent<IUserRoleProps> = (props) => {
    const appSvc = useService(AppService)
    const userRoleSvc = useService(UserRoleService)
    const accessPolicySvc = useService(AccessPolicyService)

    const userRoleId = parseInt(props.match.params.userRoleId, 10)

    const [_, runIfMounted] = useMountedRef()
    const [loading, setLoading] = React.useState(true)
    const [userRole, setUserRole] = React.useState<IUserRole | null>(null)
    const [accessPolicies, setAccessPolicies] = React.useState<IAccessPolicy[]>([])
    const [policiesToRemove, setPoliciesToRemove] = React.useState<IAccessPolicy[]>([])
    const [policiesToAttach, setPoliciesToAttach] = React.useState<IAccessPolicy[]>([])
    const [attachModalVisible, setAttachModalVisible] = React.useState(false)
    const [editorVisible, setEditorVisible] = React.useState(false)

    const onCrumbClick = React.useMemo(() => makeCrumbClick(appSvc), [])

    const loadAccessPolicies = React.useCallback(async () => {
        const { ok, data } = await accessPolicySvc.fetchAll({
            showLoadingScreen: true,
            query: { pagination: 0 },
        })

        if (ok) {
            runIfMounted(() => {
                setAccessPolicies(data)
            })
        }
    }, [])

    const loadUserRole = React.useCallback(async () => {
        const { ok, data } = await userRoleSvc.fetch(userRoleId, {
            showLoadingScreen: true,
            query: { includePolicies: 1 },
        })

        if (ok) {
            runIfMounted(() => {
                setUserRole(data)
            })
        }
    }, [userRoleId])

    const loadDependencies = React.useCallback(async () => {
        await Promise.all([loadAccessPolicies(), loadUserRole()])

        setLoading(false)
    }, [])

    const handleAttachModalClose = React.useCallback(() => {
        setAttachModalVisible(false)
        setPoliciesToAttach([])
    }, [])

    const handleUserRoleUpdate = React.useCallback(
        async (changes: Partial<IUserRole>) => {
            const res = await userRoleSvc.patch(userRole.id, changes, {
                showLoadingScreen: true,
            })

            if (res.ok) {
                await loadDependencies()
                setEditorVisible(false)
            } else if (isAxiosResponseError(res.error)) {
                simpleNotification('error', res.error.response.data.message ?? res.error.message)
            }
        },
        [userRole],
    )

    const handleRemovePolicies = React.useCallback(async () => {
        const formRef = React.createRef<FormInstance>()

        Modal.confirm({
            title: 'Remove Policies',
            icon: <WarningOutlined />,
            okText: 'Remove',
            onOk: async () => {
                const form = formRef.current

                if (form) {
                    await form.validateFields()
                    const res = await userRoleSvc.removeAccessPolicyIds(
                        userRole.id,
                        policiesToRemove.map((ap) => ap.id),
                        {
                            showLoadingScreen: true,
                        },
                    )

                    if (res.ok) {
                        await loadDependencies()
                        setPoliciesToRemove([])
                    } else if (isAxiosResponseError(res.error)) {
                        simpleNotification('error', res.error.response.data.message ?? res.error.message)
                    }
                }
            },
            content: (
                <Form ref={formRef}>
                    <p>
                        Are you sure you want to remove the following access policies? Any users currently assigned this
                        role will be affected.
                    </p>

                    <p>
                        Please type, <b>{REMOVE_KEY}</b>, to continue with this action.
                    </p>

                    <ul>
                        {policiesToRemove.map((ap) => (
                            <li key={ap.id}>{ap.name}</li>
                        ))}
                    </ul>

                    <Form.Item
                        name="name"
                        validateTrigger={false}
                        rules={[
                            {
                                validator: (_rule: RuleObject, value, callback) => {
                                    if (!value?.trim()) {
                                        callback(`${REMOVE_KEY} keyword is required`)
                                    } else if (value !== REMOVE_KEY) {
                                        callback(`You must type ${REMOVE_KEY} to continue`)
                                    } else {
                                        callback()
                                    }
                                },
                            },
                        ]}
                    >
                        <Input autoFocus={true} />
                    </Form.Item>
                </Form>
            ),
        })
    }, [userRole, policiesToRemove])

    const handleAttachPolicies = React.useCallback(async () => {
        setAttachModalVisible(false)

        Modal.confirm({
            title: 'Attach Policies',
            icon: <QuestionCircleOutlined />,
            okText: 'Attach',
            onOk: async () => {
                const res = await userRoleSvc.attachAccessPolicyIds(
                    userRole.id,
                    policiesToAttach.map((ap) => ap.id),
                    {
                        showLoadingScreen: true,
                    },
                )

                if (res.ok) {
                    await loadDependencies()
                    setPoliciesToAttach([])
                } else if (isAxiosResponseError(res.error)) {
                    simpleNotification('error', res.error.response.data.message ?? res.error.message)
                }
            },
            onCancel: () => setAttachModalVisible(true),
            content: (
                <div>
                    <p>
                        Are you sure you want to attach the following access policies? Any users currently assigned this
                        role will be affected.
                    </p>

                    <ul>
                        {policiesToAttach.map((ap) => (
                            <li key={ap.id}>{ap.name}</li>
                        ))}
                    </ul>
                </div>
            ),
        })
    }, [userRole, policiesToAttach])

    React.useEffect(() => {
        loadDependencies()
    }, [userRoleId])

    const currentPolicyIds = (userRole?.access_policies ?? []).map((ap) => ap.id)
    const currentAccessPolicies = accessPolicies.filter((policy) => currentPolicyIds.includes(policy.id))
    const availableAccessPolicies = accessPolicies.filter((policy) => !currentPolicyIds.includes(policy.id))

    return loading ? null : (
        <div className={classnames('page', 'user-role')}>
            <div className={classnames('page-wrapper')}>
                <PageHeader
                    title="User Role"
                    breadcrumb={[
                        { title: 'Access Control' },
                        { title: 'User Roles', href: '/access-control', onClick: onCrumbClick },
                        { title: userRole.description ?? userRole.name },
                    ]}
                    onTitleSet={noop}
                />

                <Flex vertical gap={VERTICAL_WELL_GAP}>
                    <Well
                        title="Details"
                        footerMode="hidden"
                        actions={[
                            <Button key="edit" size="small" onClick={() => setEditorVisible(true)}>
                                <EditOutlined />
                                <span>Edit</span>
                            </Button>,
                        ]}
                    >
                        <FormLayoutRow label="Name" value={userRole.name} />
                        <FormLayoutRow label="Description" value={userRole.description} />
                        <FormLayoutRow label="Created" value={moment(userRole.created_at).format('LLLL')} />
                        <FormLayoutRow label="User Type" value={isInternalRole(userRole) ? 'Internal' : 'External'} />
                        <FormLayoutRow label="Enabled" value={userRole.is_active ? 'TRUE' : 'FALSE'} />
                        {isInternalRole(userRole) && (
                            <FormLayoutRow
                                label="Network Scope"
                                value={userRole.has_network_scope ? 'TRUE' : 'FALSE'}
                                sub="Policies apply across every organization and domain, not only rows a user holds."
                            />
                        )}
                    </Well>

                    <UserRolePolicyEditor
                        onChange={(_policyIds, policies) => setPoliciesToRemove(policies)}
                        options={currentAccessPolicies}
                        value={policiesToRemove.map((ap) => ap.id)}
                        actions={[
                            <Button
                                key="remove"
                                size="small"
                                disabled={!policiesToRemove.length}
                                onClick={handleRemovePolicies}
                            >
                                <span>Remove</span>
                            </Button>,
                            <Button
                                key="add"
                                size="small"
                                type="primary"
                                onClick={() => {
                                    setAttachModalVisible(true)
                                    setPoliciesToRemove([])
                                }}
                            >
                                <span>Attach Policies</span>
                            </Button>,
                        ]}
                    />
                </Flex>

                <Modal
                    className={classnames('attach-user-roles-modal')}
                    title={null}
                    footer={null}
                    width={760}
                    open={attachModalVisible}
                    destroyOnClose={true}
                    onCancel={handleAttachModalClose}
                    styles={{ body: { padding: 0, maxHeight: '70vh', overflow: 'auto' } }}
                >
                    <UserRolePolicyEditor
                        title="Attach Access Policies"
                        onChange={(_policyIds, policies) => setPoliciesToAttach(policies)}
                        options={availableAccessPolicies}
                        value={policiesToAttach.map((ap) => ap.id)}
                        actions={[
                            <Button key="cancel" size="small" onClick={handleAttachModalClose}>
                                <span>Cancel</span>
                            </Button>,
                            <Button
                                key="add"
                                size="small"
                                type={!policiesToAttach.length ? null : 'primary'}
                                disabled={!policiesToAttach.length}
                                onClick={handleAttachPolicies}
                            >
                                <span>Attach</span>
                            </Button>,
                        ]}
                    />
                </Modal>

                <UserRoleEditor
                    userRole={userRole}
                    visible={editorVisible}
                    onClose={() => setEditorVisible(false)}
                    onSubmit={handleUserRoleUpdate}
                />
            </div>
        </div>
    )
}

export default UserRoleView
